import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { FormControl } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { red } from "@mui/material/colors";
import {
  calculateSellerCardProcessingFee,
  getBuyerCapturedAmountForCardFee,
  getNextBusinessDays,
  getDisplayProduct,
  isCardPaymentOrder,
} from "../../../components/utils/utils";
import { FedexPickupType } from "../../../constants/shipping";

interface ActionRequiredOrdersProps {
  availableItems: any;
  setFedExInsurance: any;
  setFedExPickupType: any;
  setFedExPickupDate: any
}

export const ConfirmOrder: React.FunctionComponent<ActionRequiredOrdersProps> = ({
  availableItems,
  setFedExInsurance,
  setFedExPickupType,
  setFedExPickupDate,
}) => {
  const isFrozen = availableItems.find((item: any) => getDisplayProduct(item).isFrozen);
  const shipDates = getNextBusinessDays(isFrozen);
  const [shipDate, setShipDate] = useState<any>(shipDates[0].format('MM/DD/YYYY'));
  const [insurance, setInsurance] = useState<boolean>(false);
  const [dropOff, setDropOff] = React.useState(FedexPickupType.dropOffAtFedexLocation);

  useEffect(() => {
    setFedExPickupDate(shipDate)
  }, [setFedExPickupDate, shipDate]);

  const getTotal = (items: any) => {
    let total = 0;
    items.filter((item: any) => item.status === 1).forEach((item: any) => {
      total = total + Number(item.totalAmount);
    });

    return total;
  }

  const calculateFedexInsurance = (total: number) => {
    if (total < 100) {
      return 0;
    }

    return Math.ceil(1.25 * (total - 100) / 100)
  }

  const handleChange = (event: SelectChangeEvent) => {
    setDropOff(event.target.value as FedexPickupType.dropOffAtFedexLocation);
    setFedExPickupType(event.target.value as string);
  };

  const firstItem = availableItems[0];
  const isCardPayment = isCardPaymentOrder(firstItem);
  const capturedAmountForCardFee = getBuyerCapturedAmountForCardFee(
    availableItems,
    firstItem.ShippingMethod?.key ?? '',
    firstItem.isShippingPaidBySeller === true
  );
  const stripeCardFee = isCardPayment
    ? calculateSellerCardProcessingFee(capturedAmountForCardFee)
    : 0;

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ mt: 1 }}>
        <Box sx={{ opacity: 0.5, fontSize: 12 }}>Shipping Method</Box>
        <Box sx={{ fontSize: 14, mt: 1 }}>{availableItems[0].ShippingMethod.name}</Box>
      </Box>
      <Box sx={{ mt: 1 }}>
        <FormControlLabel
          control={(
            <Checkbox
              checked={insurance}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setInsurance(event.target.checked);
                setFedExInsurance(event.target.checked ? calculateFedexInsurance(getTotal(availableItems)) : 0);
              }}
            />
          )}
          label="Pay for FedEx Insurance"
        />
      </Box>
      {isFrozen && (
        <Box sx={{ mt: 2, fontSize: '12px', color: red[500] }}>Please be aware that there is a frozen item in your list. To ensure its proper handling, kindly arrange for a drop-off or schedule a pick-up between Monday and Thursday. </Box>
      )}
      <Box sx={{ mt: 2 }}>
        <Box sx={{ opacity: 0.5, fontSize: 14 }}>
          Package ready for pick up at (Please allow up to 2 hour(s) for pick up)
        </Box>
        <Box sx={{ mt: 1 }}>
          <Box sx={{ opacity: 0.5, fontSize: 12 }}>Ship Date</Box>
          <RadioGroup
            aria-labelledby='type-label'
            row id='type'
            sx={{
              '& .MuiSvgIcon-root': {
                fontSize: 20,
              },
            }}
            value={shipDate}
            onChange={(event) => {
              setShipDate(event.target.value);
              setFedExPickupDate(event.target.value);
            }}
          >
            {shipDates.map((shipDate: any, index: number) => (
              <FormControlLabel
                key={index}
                value={shipDate.format('MM/DD/YYYY')}
                control={<Radio />}
                label={`${shipDate.format('dddd').substring(0, 3)} ${shipDate.format('MM/DD/YYYY')}`}
              />
            ))}
          </RadioGroup>
          <Box sx={{ fontSize: 12, color: red[500] }}>Make sure none of the dates are holidays.</Box>
        </Box>
        <Box sx={{ minWidth: 120, mt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="drop-fedex-label">Pickup Types</InputLabel>
            <Select
              labelId="drop-fedex-label"
              id="demo-select-small"
              value={dropOff}
              label='Pickup Types'
              onChange={handleChange}
            >
              <MenuItem value={FedexPickupType.dropOffAtFedexLocation}>Drop off at a FedEx Location</MenuItem>
              <MenuItem value={FedexPickupType.contactFedexToSchedule}>Contact FedEx to schedule a pick up</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box sx={{ padding: 2, borderRadius: '8px', border: '1px solid #CCAF9B', mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ opacity: 0.6, fontSize: 14 }}>Item(s) Total</Box>
          <Box sx={{ fontSize: 14 }}>${getTotal(availableItems).toFixed(2)}</Box>
        </Box>
        {!isCardPayment && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Box sx={{ opacity: 0.6, fontSize: 14 }}>Transaction fee</Box>
            <Box sx={{ fontSize: 14 }}>-$10.00</Box>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
          <Box sx={{ opacity: 0.6, fontSize: 14 }}>TradeNetRx fee ({availableItems[0].tradeNetFee}%)</Box>
          <Box sx={{ fontSize: 14 }}>-${(getTotal(availableItems) * availableItems[0].tradeNetFee / 100).toFixed(2)}</Box>
        </Box>
        {(availableItems[0].ShippingMethod.key === 'fedex-ground' ||
          availableItems[0].isShippingPaidBySeller) && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Box sx={{ opacity: 0.6, fontSize: 14 }}>Shipping Fee</Box>
            <Box sx={{ fontSize: 14 }}>-${availableItems.reduce(
              (sum: any, order: any) => sum + parseFloat(order.shippingFee || '0'),
              0
            ).toFixed(2)}</Box>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
          <Box sx={{ opacity: 0.6, fontSize: 14 }}>Fedex insurance</Box>
          <Box sx={{ fontSize: 14 }}>{insurance ? `-$${calculateFedexInsurance(getTotal(availableItems)).toFixed(2)}` : '$0.00'}</Box>
        </Box>
        {isCardPayment && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Box sx={{ opacity: 0.6, fontSize: 14 }}>
              Transaction fee
            </Box>
            <Box sx={{ fontSize: 14 }}>-${stripeCardFee.toFixed(2)}</Box>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
        <Box sx={{ fontSize: 16 }}>Credit Total</Box>
        <Box sx={{ fontSize: 16 }}>
          {(() => {
            const total = getTotal(availableItems);
            const tradeNetFee = (total * availableItems[0].tradeNetFee) / 100;
            const transactionFee = isCardPayment ? 0 : 10;
            const fedexInsurance = insurance ? calculateFedexInsurance(total) : 0;
            const shippingFee =
              (availableItems[0].ShippingMethod.key === 'fedex-ground' ||
                availableItems[0].isShippingPaidBySeller)
                ? availableItems.reduce(
                  (sum: any, order: any) => sum + parseFloat(order.shippingFee || '0'),
                  0
                )
                : 0;

            const creditTotal =
              total -
              tradeNetFee -
              transactionFee -
              fedexInsurance -
              shippingFee -
              stripeCardFee;
            return `$${creditTotal.toFixed(2)}`;
          })()}
        </Box>
      </Box>
    </Box>
  );
}